import { FC, useEffect, useState } from 'react';
import { Button, Divider, Popover, TextField, IconButton, InputAdornment } from '@mui/material';
import { labelColors, TaskLabel } from '@demind-inc/core';

import './ColorPickerDropdown.scss';
import { CheckOutlined, DeleteOutline, EditOutlined } from '@mui/icons-material';
import { upperFirst } from 'lodash';
import { useTranslation } from 'react-i18next';
import { MAX_LABEL_NAME_LENGTH } from '../../constants';
import { BasicDialog } from '../../../../common';

interface ColorPickerDropdownProps {
  label: TaskLabel;
  anchorEl: HTMLElement | null;
  onClose: (event: React.MouseEvent<HTMLElement>) => void;
  onColorChange: (color: string) => void;
  onUpdateLabel?: (label: TaskLabel) => void;
  onDeleteLabel?: (label: TaskLabel) => Promise<void>;
  isDeletingLabel?: boolean;
}

const ColorPickerDropdown: FC<ColorPickerDropdownProps> = ({
  label,
  anchorEl,
  onClose,
  onColorChange,
  onUpdateLabel = () => void 0,
  onDeleteLabel = async () => void 0,
  isDeletingLabel = false,
}) => {
  const { t } = useTranslation();
  const isDefaultLabel = label?.id.includes('default_');
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameInput, setNameInput] = useState(label.name || '');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  useEffect(() => {
    setIsEditingName(false);
    setNameInput(label.name || '');
  }, [label]);

  const handleEditClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsEditingName(true);
  };

  const handleNameSave = () => {
    const trimmed = nameInput.trim();
    if (!trimmed) {
      setNameInput(label.name || '');
      setIsEditingName(false);
      return;
    }
    if (trimmed.length > MAX_LABEL_NAME_LENGTH) {
      return;
    }
    const nextName = trimmed;
    if (nextName === label.name) {
      setIsEditingName(false);
      return;
    }

    onUpdateLabel({
      ...label,
      name: nextName,
    });
    setIsEditingName(false);
  };

  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      handleNameSave();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setIsEditingName(false);
      setNameInput(label.name || '');
    }
  };

  const handleDelete = async () => {
    await onDeleteLabel(label);
    setIsDeleteDialogOpen(false);
  };

  return (
    <>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        key="color-picker-dropdown"
        onClose={onClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            className: 'color-picker-dropdown__menu',
          },
        }}
        className="color-picker-dropdown"
      >
        <div className="color-picker-dropdown__content">
          <div className="color-picker-dropdown__header">
            <div className="color-picker-dropdown__label">
              {isEditingName ? (
                <TextField
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  onKeyDown={handleNameKeyDown}
                  autoFocus
                  size="small"
                  variant="standard"
                  fullWidth
                  onBlur={handleNameSave}
                  inputProps={{ 'data-ignore-select': 'true' }}
                  InputProps={{
                    disableUnderline: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          size="small"
                          color="primary"
                          disabled={
                            !nameInput.trim() || nameInput.trim().length > MAX_LABEL_NAME_LENGTH
                          }
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleNameSave();
                          }}
                        >
                          <CheckOutlined fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              ) : (
                label.name
              )}
            </div>
            {!isDefaultLabel && !isEditingName && (
              <div className="color-picker-dropdown__action" onClick={handleEditClick}>
                <EditOutlined className="color-picker-dropdown__action__icon" />
              </div>
            )}
          </div>
          <Divider className="color-picker-dropdown__divider" />
          <div className="color-picker-dropdown__colors">
            <div className="color-picker-dropdown__colors__label">{t('task.labels.colors')}</div>
            <div className="color-picker-dropdown__colors__list">
              {Object.entries(labelColors).map(([name, color]) => (
                <div
                  key={name}
                  className="color-picker-dropdown__colors__list__item"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onColorChange(color);
                    onClose(e);
                  }}
                >
                  <div className="color-picker-dropdown__colors__list__item__left">
                    <div
                      key={color}
                      className="color-picker-dropdown__colors__list__item__color"
                      style={{ backgroundColor: color }}
                    />
                    <div className="color-picker-dropdown__colors__list__item__name">
                      {t(`task.labels.colorNames.${name}`, { defaultValue: upperFirst(name) })}
                    </div>
                  </div>
                  {color === label.color && (
                    <CheckOutlined className="color-picker-dropdown__colors__list__item__check" />
                  )}
                </div>
              ))}
            </div>
          </div>
          {!isDefaultLabel && (
            <div className="color-picker-dropdown__delete">
              <Divider className="color-picker-dropdown__divider" />
              <Button
                startIcon={
                  <DeleteOutline className="color-picker-dropdown__delete__button__icon" />
                }
                color="error"
                fullWidth
                disabled={isDeletingLabel}
                className="color-picker-dropdown__delete__button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsDeleteDialogOpen(true);
                }}
              >
                {t('task.common.delete')}
              </Button>
            </div>
          )}
        </div>
      </Popover>
      {!isDefaultLabel && (
        <BasicDialog
          visible={isDeleteDialogOpen}
          title={t('task.labels.deleteLabel')}
          description={t('task.labels.deleteLabelDescription')}
          ctaString={t('task.common.delete')}
          width="xs"
          isLoading={isDeletingLabel}
          onClose={() => setIsDeleteDialogOpen(false)}
          onYes={handleDelete}
        />
      )}
    </>
  );
};

export default ColorPickerDropdown;
